import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate unique IDs
const generateId = (prefix) => {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create visitor ID (persists across sessions)
const getOrCreateVisitorId = () => {
  let visitorId = localStorage.getItem("calli_visitor_id");
  if (!visitorId) {
    visitorId = generateId("v");
    localStorage.setItem("calli_visitor_id", visitorId);
  }
  return visitorId;
};

// Get or create session ID (expires after 30 minutes of inactivity)
const getOrCreateSessionId = () => {
  let sessionId = sessionStorage.getItem("calli_session_id");
  let sessionExpiry = sessionStorage.getItem("calli_session_expiry");

  if (!sessionId || !sessionExpiry || Date.now() > parseInt(sessionExpiry)) {
    sessionId = generateId("s");
    sessionStorage.setItem("calli_session_id", sessionId);
  }

  // Extend session
  sessionStorage.setItem("calli_session_expiry", (Date.now() + 30 * 60 * 1000).toString());
  return sessionId;
};

// Get UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_term: params.get("utm_term"),
    utm_content: params.get("utm_content"),
  };
};

// Get browser language
const getBrowserLanguage = () => {
  return navigator.language || navigator.userLanguage || "unknown";
};

// Get timezone
const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return "unknown";
  }
};

// Send analytics data
const sendAnalytics = async (endpoint, data) => {
  try {
    await fetch(`${API}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      keepalive: true,
    });
  } catch (e) {
    // Silently fail - analytics should not break the app
  }
};

// Main analytics hook
export const useAnalytics = () => {
  const location = useLocation();
  const visitorIdRef = useRef(getOrCreateVisitorId());
  const sessionIdRef = useRef(getOrCreateSessionId());
  const sessionStartRef = useRef(Date.now());
  const pageLoadTimeRef = useRef(Date.now());
  const pagesViewedRef = useRef(1);
  const maxScrollDepthRef = useRef(0);
  const previousPageRef = useRef(null);
  const isFirstPageRef = useRef(true);

  // Track page view
  const trackPageView = useCallback((isEntry = false) => {
    const utmParams = getUTMParams();

    sendAnalytics("/analytics/track", {
      page: location.pathname,
      referrer: document.referrer || "direct",
      user_agent: navigator.userAgent,
      visitor_id: visitorIdRef.current,
      session_id: sessionIdRef.current,
      ...utmParams,
      language: getBrowserLanguage(),
      timezone: getTimezone(),
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      is_entry_page: isEntry || isFirstPageRef.current,
      previous_page: previousPageRef.current,
    });

    isFirstPageRef.current = false;
  }, [location.pathname]);

  // Track page exit
  const trackPageExit = useCallback((isExitPage = false) => {
    const timeOnPage = (Date.now() - pageLoadTimeRef.current) / 1000;

    sendAnalytics("/analytics/page-exit", {
      visitor_id: visitorIdRef.current,
      session_id: sessionIdRef.current,
      page: location.pathname,
      time_on_page: timeOnPage,
      scroll_depth: maxScrollDepthRef.current,
      is_exit_page: isExitPage,
    });
  }, [location.pathname]);

  // Track session end
  const trackSessionEnd = useCallback(() => {
    const totalDuration = (Date.now() - sessionStartRef.current) / 1000;

    sendAnalytics("/analytics/session-end", {
      visitor_id: visitorIdRef.current,
      session_id: sessionIdRef.current,
      total_duration: totalDuration,
      pages_viewed: pagesViewedRef.current,
      exit_page: location.pathname,
    });
  }, [location.pathname]);

  // Initialize scroll tracking
  useEffect(() => {
    let ticking = false;

    const updateScrollDepth = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      ) - window.innerHeight;

      if (docHeight > 0) {
        const scrollPercent = Math.round((scrollTop / docHeight) * 100);
        maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, Math.min(scrollPercent, 100));
      }
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollDepth);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Track page views on route change
  useEffect(() => {
    // Reset page-level tracking
    pageLoadTimeRef.current = Date.now();
    maxScrollDepthRef.current = 0;

    // Track the new page view
    trackPageView(isFirstPageRef.current);

    // After first page, update counters
    if (!isFirstPageRef.current) {
      pagesViewedRef.current++;
    }
  }, [location.pathname, trackPageView]);

  // Track page exit on route change (for previous page)
  useEffect(() => {
    return () => {
      if (previousPageRef.current) {
        trackPageExit(false);
      }
      previousPageRef.current = location.pathname;
    };
  }, [location.pathname, trackPageExit]);

  // Track session end on window close
  useEffect(() => {
    const handleBeforeUnload = () => {
      trackPageExit(true);
      trackSessionEnd();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [trackPageExit, trackSessionEnd]);

  // Track visibility change (tab hidden)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        trackPageExit(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [trackPageExit]);
};

// Track specific events (clicks, conversions, etc.)
export const trackEvent = async (eventType, metadata = {}) => {
  const visitorId = getOrCreateVisitorId();
  const sessionId = getOrCreateSessionId();

  await sendAnalytics("/analytics/event", {
    event_type: eventType,
    page: window.location.pathname,
    visitor_id: visitorId,
    session_id: sessionId,
    element_id: metadata.element_id,
    element_text: metadata.element_text,
    metadata: metadata,
    value: metadata.value,
  });
};

// Track conversion events
export const trackConversion = async (conversionType, value = null, metadata = {}) => {
  await trackEvent(conversionType, {
    ...metadata,
    value: value,
    is_conversion: true,
  });
};

// Predefined event trackers
export const trackPreorderClick = () => trackEvent("preorder_click");
export const trackAuthOpen = () => trackEvent("auth_open");
export const trackSignup = (method = "email") => trackEvent("signup", { method });
export const trackGoogleAuth = () => trackEvent("google_auth");
export const trackCheckoutStart = () => trackEvent("checkout_start");
export const trackPreorderComplete = (amount) => trackConversion("preorder_complete", amount);
export const trackVideoPlay = (videoId) => trackEvent("video_play", { video_id: videoId });
export const trackVideoComplete = (videoId) => trackEvent("video_complete", { video_id: videoId });
export const trackFormSubmit = (formId) => trackEvent("form_submit", { form_id: formId });
export const trackButtonClick = (buttonId, buttonText) => trackEvent("button_click", { element_id: buttonId, element_text: buttonText });

// Analytics wrapper component
export const AnalyticsTracker = ({ children }) => {
  useAnalytics();
  return children;
};

export default useAnalytics;
